// User menu dropdown — opened by clicking the avatar in the TopNav.
//
// Sits inline below the avatar with click-outside / Esc dismissal.
// Holds only the person's own account (navigation overhaul, 2026-10-15):
//   * Profile / My settings / Security & 2FA / My commissions
//   * Keyboard shortcuts (opens the cheat sheet via ChromeContext)
//   * Dark mode toggle (saves to the ACCOUNT and sets an `html.dark`
//     class, which activates the dark token block in styles.css. Dark is
//     strictly opt-in — new users always start light; see the
//     DECISION note in user-menu-helpers.ts, and settings/use-dark-mode.ts
//     for why a localStorage paint cache still exists alongside it).
//   * Help & docs (the in-app help guide at /help)
//   * Send feedback (mailto)
//   * Sign out (calls existing signOut)
//
// Pure helpers (initials, role label) live in top-nav-helpers.ts.
// Dark-mode persistence pure logic lives in user-menu-helpers.ts.

import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { resolveMediaUrl, type SessionUser } from "../api";
import { useChrome } from "./ChromeContext";
import { useDarkMode } from "../settings/use-dark-mode";
import { displayRoleLabel, initialsFor } from "./top-nav-helpers";
import { detectMacLikePlatform, shortcutModifierLabel } from "../hooks/keyboard-shortcuts-helpers";

interface UserMenuProps {
  user: SessionUser;
  onSignOut: () => void;
}

export function UserMenu({ user, onSignOut }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  // Theme follows the account (2026-08-15); the hook also keeps
  // <html class="dark"> and the pre-hydration paint cache in step.
  const { isDark, toggle: handleToggleDark } = useDarkMode();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();
  const chrome = useChrome();

  // Outside-click + Esc to close.
  useEffect(() => {
    if (!open) return;
    function onMouseDown(event: MouseEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  const handleKeyboardShortcuts = useCallback(() => {
    close();
    chrome.openCheatSheet();
  }, [close, chrome]);

  const handleSendFeedback = useCallback(() => {
    close();
    // mailto: deliberately — see cutover-checklist §4. The eventual
    // surface routes feedback into Linear / Slack.
    window.location.href = "mailto:feedback@stewardship.pro";
  }, [close]);

  const handleSignOut = useCallback(() => {
    setOpen(false);
    onSignOut();
  }, [onSignOut]);

  const displayName = user.name ?? user.email;
  const roleLabel = displayRoleLabel(user.employee_role_slug);
  const status = "Online · Stewardship";
  // Ctrl on Windows, ⌘ on a Mac — the same detection Ask Stew's button
  // uses, so the hint names the key the person actually presses.
  const modifier = useMemo(() => shortcutModifierLabel(detectMacLikePlatform()), []);

  return (
    <div className="user-menu-wrap">
      <button
        ref={triggerRef}
        type="button"
        className="top-nav-user-toggle"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
        data-testid="top-nav-user-toggle"
      >
        <span className="top-nav-avatar" aria-hidden="true" data-testid="top-nav-avatar">
          {user.image ? (
            <img
              className="top-nav-avatar-img"
              src={resolveMediaUrl(user.image) ?? user.image}
              alt=""
            />
          ) : (
            initialsFor(user)
          )}
        </span>
        <ChevronDown size={14} strokeWidth={1.5} aria-hidden />
      </button>
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Account"
          className="user-menu"
          data-testid="user-menu"
        >
          <div className="user-menu-header">
            <div className="user-menu-name">{displayName}</div>
            {roleLabel && <div className="user-menu-role">{roleLabel}</div>}
            <div className="user-menu-status">{status}</div>
          </div>
          <ul className="user-menu-list">
            <li>
              <Link
                role="menuitem"
                to="/settings?tab=profile"
                className="user-menu-item"
                onClick={close}
              >
                {/* No shortcut hint: "⌘ P" was printed here but never bound
                    to anything, and Ctrl+P is the browser's Print. */}
                <span>Profile</span>
              </Link>
            </li>
            <li>
              <Link
                role="menuitem"
                to="/settings?tab=preferences"
                className="user-menu-item"
                onClick={close}
              >
                {/* "My settings" (2026-10-15): this page changes things for
                    you only. Firm-wide settings are Admin → OS settings. */}
                <span>My settings</span>
                <kbd className="kbd">{`${modifier} ,`}</kbd>
              </Link>
            </li>
            <li>
              <Link role="menuitem" to="/security" className="user-menu-item" onClick={close}>
                <span>Security &amp; 2FA</span>
              </Link>
            </li>
            <li>
              {/* 2026-08-23 — "what am I getting paid" is account
                  information, so it belongs in this menu with the rest
                  of a person's own things. Before this it was in no menu
                  at all: not the top bar, not the rail, not here. The
                  only ways to reach it were typing the URL or asking
                  Stew, which is not a way to find something you didn't
                  know existed. */}
              <Link role="menuitem" to="/my-commissions" className="user-menu-item" onClick={close}>
                <span>My commissions</span>
              </Link>
            </li>
            <li>
              {/* Build step B2: each person sees and signs only their own
                  comp plan (Grant, 2026-10-15). Account information, so
                  it sits with "My commissions". */}
              <Link role="menuitem" to="/my-comp-plan" className="user-menu-item" onClick={close}>
                <span>My comp plan</span>
              </Link>
            </li>
            {/* 2026-10-15 navigation overhaul: this menu holds only the
                person's own account. Checklists moved to Tasks, PM
                Guidelines and KlinkLoans Fund to Company tools → Mortgage,
                and License lookup to Company tools → Every company — each
                with the same gate it had here (see sidebar-data.ts). */}
            <li>
              <button
                type="button"
                role="menuitem"
                className="user-menu-item"
                onClick={handleKeyboardShortcuts}
              >
                <span>Keyboard shortcuts</span>
                <kbd className="kbd">?</kbd>
              </button>
            </li>
            <li>
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={isDark}
                className="user-menu-item user-menu-item-toggle"
                onClick={handleToggleDark}
                data-testid="dark-mode-toggle"
              >
                <span>Dark mode</span>
                <span className={`user-menu-switch${isDark ? " is-on" : ""}`} aria-hidden="true" />
              </button>
            </li>
            <li>
              {/* 2026-08-03 — was a "Docs site coming in v1.1." toast;
                  now the in-app help guide (same corpus Ask Stew
                  answers how-to questions from). */}
              <Link role="menuitem" to="/help" className="user-menu-item" onClick={close}>
                Help &amp; docs
              </Link>
            </li>
            <li>
              <button
                type="button"
                role="menuitem"
                className="user-menu-item"
                onClick={handleSendFeedback}
              >
                Send feedback
              </button>
            </li>
            <li className="user-menu-separator" aria-hidden="true" />
            <li>
              <button
                type="button"
                role="menuitem"
                className="user-menu-item user-menu-item-signout"
                onClick={handleSignOut}
              >
                Sign out
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
